import { User } from "features/User/models";
import { Company } from "models/company";
import ROLE from "models/role";
import { create } from "zustand";
import { persist } from "zustand/middleware";
import { pick } from "lodash";
import apiClient from "services/ApiClient";

export type CredentialType = "work_email" | "personal_email";

interface UserBundle {
  user: User;
  features: string[];
  credentialType: CredentialType | null;
}

interface CaminoStore {
  // User data (only essential fields persisted)
  currentUser: Partial<User> | null;
  isImpersonating: boolean;
  currentRole: ROLE | null;
  currentCompany: Partial<Company> | null;
  credentialType: CredentialType | null;

  // Feature flags (not persisted)
  enabledFeatures: string[];

  // Loading states
  isLoading: boolean;
  error: string | null;

  // Methods
  fetchUserBundle: () => Promise<void>;
  updateCurrentUser: (user: Partial<User> | null) => void;
  setValue: (value: Partial<CaminoStore>) => void;
  isFeatureEnabled: (featureName: string) => boolean;
  supportedRoles: () => ROLE[];
  clearStore: () => void;
}

type CaminoStoreValues = Pick<
CaminoStore,
"currentUser" | "currentRole" | "currentCompany" | "isImpersonating" | "credentialType"
>;

const _supportedRoles: () => ROLE[] = () => [
  ROLE.MANAGER,
  ROLE.PEOPLE_TEAM,
  ROLE.CAMINO_TEAM,
  ROLE.IT_ADMIN,
  ROLE.ONBOARDER,
  ROLE.BASIC,
];

const _currentRole = (state: CaminoStore, user: Partial<User> | null = null) => {
  if (!user) return null;

  const supportedRoles = _supportedRoles();
  if (state?.currentRole && supportedRoles.includes(state.currentRole)) return state.currentRole;

  const permissions = new Set([
    ...(user.permission || []),
    ...(state?.currentUser?.permission || []),
  ]);
  return supportedRoles.find((role) => permissions.has(role)) || null;
};

const multiKeyStorage = <T extends object>(keys: (keyof T)[], prefix: string) => {
  const keyFor = (k: keyof T) => `${prefix}:${String(k)}`;

  return {
    getItem: (_name: string) => {
      const state: Record<string, unknown> = {};
      keys.forEach((k) => {
        const raw = localStorage.getItem(keyFor(k));
        if (raw != null) {
          try {
            state[String(k)] = JSON.parse(raw);
          } catch (e) {
            console.error(`Failed to parse localStorage key ${keyFor(k)}:`, e);
            // Skip this key if parsing fails
          }
        }
      });
      return { state, version: 0 };
    },

    setItem: (_name: string, value: any): void => {
      try {
        // Handle both string and object inputs
        let parsed: any;
        if (typeof value === "string") {
          parsed = JSON.parse(value);
        } else {
          parsed = value; // Already an object
        }

        const state = parsed.state || parsed; // Handle both { state: ... } and direct state object

        keys.forEach((k) => {
          if (k in state) {
            const valueToStore = state[String(k)];
            // Only store if value is not undefined
            if (valueToStore !== undefined) {
              try {
                localStorage.setItem(keyFor(k), JSON.stringify(valueToStore));
              } catch (e) {
                console.error(`Failed to save localStorage key ${keyFor(k)}:`, e);
              }
            }
          }
        });
      } catch (e) {
        console.error("Failed to process value for storage:", e);
        console.error("Value that failed:", value);
      }
    },

    removeItem: (_name: string): void => {
      keys.forEach((k) => localStorage.removeItem(keyFor(k)));
    },
  };
};

const useCaminoStore = create<CaminoStore>()(
  persist(
    (set, get) => ({
      // Persisted state
      currentUser: null,
      isImpersonating: false,
      currentRole: null,
      currentCompany: null,
      credentialType: null,
      enabledFeatures: [],

      isLoading: false,
      error: null,

      fetchUserBundle: async () => {
        set({ isLoading: true, error: null });

        try {
          const response = await apiClient.get<UserBundle>("/api/user_bundle");
          const userBundle = response;

          const { user } = userBundle;
          const currentRole = _currentRole(get(), user);

          set({
            currentUser: user,
            currentCompany: user.company,
            currentRole,
            credentialType: userBundle.credentialType ?? null,
            enabledFeatures: userBundle.features || [],
            isLoading: false,
          });
        } catch (error: any) {
          // If we get a 401, the user is not authenticated - clear the store and let the app redirect to login
          if (error.status === 401) {
            get().clearStore();
            set({ isLoading: false });
            return;
          }

          set({
            error: error.message || "Failed to fetch user bundle",
            isLoading: false,
          });
        }
      },

      updateCurrentUser: (user: Partial<User> | null) => {
        if (!user) {
          set({ currentUser: null, currentRole: null });
          return;
        }
        const state = get();

        const tempUser = { ...state.currentUser, ...user };
        if (Object.keys(tempUser).length === 0) {
          set({ currentUser: null, currentRole: null });
        } else {
          const currentRole = _currentRole(state, tempUser);
          set({
            currentUser: tempUser,
            currentRole,
          });
        }
      },

      setValue: (value: Partial<CaminoStoreValues>) => {
        const previousState = get();
        const { currentUser } = value;
        const company = value.currentCompany || currentUser?.company;

        if (currentUser) {
          previousState.updateCurrentUser(currentUser);
        }
        if (value.currentRole) {
          const currentRole = _supportedRoles().includes(value.currentRole) ? value.currentRole : _currentRole(previousState, currentUser);
          set({ currentRole });
        } else if (
          (currentUser && !value.currentRole)
          || (previousState.currentRole && !currentUser?.permission?.includes(previousState.currentRole))
        ) {
          const oldState = get();
          const currentRole = _currentRole(oldState, currentUser);
          set({ currentRole });
        }
        if (company) {
          set({ currentCompany: company });
        }
        if (value.isImpersonating !== undefined) {
          set({ isImpersonating: value.isImpersonating });
        }
        if (value.credentialType !== undefined) {
          set({ credentialType: value.credentialType });
        }
      },

      isFeatureEnabled: (featureName: string) => {
        const { enabledFeatures } = get();
        return enabledFeatures.includes(featureName);
      },

      supportedRoles: () => _supportedRoles(),
      clearStore: () => {
        set({
          currentUser: null,
          currentRole: null,
          currentCompany: null,
          credentialType: null,
          isImpersonating: false,
          enabledFeatures: [],
          error: null,
        });
      },
    }),
    {
      name: "camino-storage",
      storage: multiKeyStorage<CaminoStore>(
        ["currentUser", "currentRole", "currentCompany", "enabledFeatures", "isImpersonating"],
        "camino-storage",
      ),
      // Only persist specific fields — credentialType is intentionally excluded (session-bound, must come fresh from the server)
      partialize: (state) => ({
        currentUser: state.currentUser ? pick(state.currentUser, [
          "id",
          "firstName",
          "lastName",
          "fullName",
          "title",
          "permission",
          "companyId",
          "personalEmail",
          "workEmail",
          "avatarUrl",
        ]) : null,
        currentRole: state.currentRole,
        currentCompany: state.currentCompany ? pick(
          state.currentCompany,
          ["id", "name", "slug", "logoUrl"],
        ) : null,
        enabledFeatures: state.enabledFeatures,
        isImpersonating: state.isImpersonating,
      }),
    },
  ),
);

export default useCaminoStore;
